/**
 * ResultsPanel component for displaying game results and actions.
 */

import {
  Box,
  Button,
  Paper,
  Typography,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import FinishIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import OvertimeIcon from '@mui/icons-material/Timer';
import type { LocalGameState } from '@/types';
import { getHalfDisplayScore } from '@/types';

interface ResultsPanelProps {
  game: LocalGameState;
  onSubmitScore: () => void;
  onSubmitOvertime: () => void;
  onCancelGame: () => void;
  isSubmitting?: boolean;
}

export function ResultsPanel({
  game,
  onSubmitScore,
  onSubmitOvertime,
  onCancelGame,
  isSubmitting = false,
}: ResultsPanelProps) {
  // Calculate display scores for each pääty
  const team1FirstHalf = game.firstHalf.ready
    ? getHalfDisplayScore(game.firstHalf.team1)
    : null;
  const team2FirstHalf = game.firstHalf.ready
    ? getHalfDisplayScore(game.firstHalf.team2)
    : null;

  const team1SecondHalf = game.secondHalf.ready
    ? getHalfDisplayScore(game.secondHalf.team1)
    : null;
  const team2SecondHalf = game.secondHalf.ready
    ? getHalfDisplayScore(game.secondHalf.team2)
    : null;

  const team1Overtime = game.overtime.ready
    ? getHalfDisplayScore(game.overtime.team1)
    : null;
  const team2Overtime = game.overtime.ready
    ? getHalfDisplayScore(game.overtime.team2)
    : null;

  // Calculate totals
  let team1Total: number | null = null;
  let team2Total: number | null = null;

  if (game.overtime.ready && team1Overtime !== null && team2Overtime !== null) {
    // Overtime: winner is decided by overtime score only
    team1Total = team1Overtime;
    team2Total = team2Overtime;
  } else if (team1FirstHalf !== null && team2FirstHalf !== null &&
             team1SecondHalf !== null && team2SecondHalf !== null) {
    // Regular game: sum of both päätys
    team1Total = team1FirstHalf + team1SecondHalf;
    team2Total = team2FirstHalf + team2SecondHalf;
  }

  const isTied = team1Total !== null && team2Total !== null && team1Total === team2Total;
  const bothHalvesReady = game.firstHalf.ready && game.secondHalf.ready;
  
  // Overtime rules differ by league:
  // - PRO and Firma: overtime from round 2+ (round 1 allows ties)
  // - Yleinen (yle, yle09, yle12): overtime from round 3+ (rounds 1-2 allow ties)
  const getOvertimeMinRound = (league: string): number => {
    if (league === 'pro' || league === 'frm') return 2;
    return 3; // yle, yle09, yle12, or unknown defaults to Yleinen rules
  };
  const showOvertime = game.round >= getOvertimeMinRound(game.league ?? 'yle');
  const canSubmit = bothHalvesReady && (!isTied || !showOvertime);
  // Need overtime if tied and round 3+ - even if OT was already played but still tied
  const needsOvertime = bothHalvesReady && isTied && showOvertime;

  // Determine winner (higher score wins in kyykkä)
  let winner: 'team1' | 'team2' | null = null;
  if (team1Total !== null && team2Total !== null && team1Total !== team2Total) {
    winner = team1Total > team2Total ? 'team1' : 'team2';
  }

  return (
    <Box>
      {/* Score Summary */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Tulosyhteenveto
        </Typography>

        {/* Teams and scores table */}
        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Joukkue</TableCell>
              <TableCell align="center">1. pääty</TableCell>
              <TableCell align="center">2. pääty</TableCell>
              {game.overtime.ready && <TableCell align="center">JA</TableCell>}
              <TableCell align="center">Yht.</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Team 1 */}
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: winner === 'team1' || isTied ? 700 : 400,
                  color: winner === 'team1' ? 'success.main' : 'text.primary',
                  backgroundColor: isTied && bothHalvesReady ? 'warning.light' : 'inherit',
                }}
              >
                {game.team1.name}
              </TableCell>
              <TableCell align="center">{team1FirstHalf ?? '-'}</TableCell>
              <TableCell align="center">{team1SecondHalf ?? '-'}</TableCell>
              {game.overtime.ready && (
                <TableCell align="center">{team1Overtime ?? '-'}</TableCell>
              )}
              <TableCell
                align="center"
                sx={{
                  fontWeight: 700,
                  color: winner === 'team1' ? 'success.main' : 'text.primary',
                }}
              >
                {team1Total ?? '-'}
              </TableCell>
            </TableRow>

            {/* Team 2 */}
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: winner === 'team2' || isTied ? 700 : 400,
                  color: winner === 'team2' ? 'success.main' : 'text.primary',
                  backgroundColor: isTied && bothHalvesReady ? 'warning.light' : 'inherit',
                }}
              >
                {game.team2.name}
              </TableCell>
              <TableCell align="center">{team2FirstHalf ?? '-'}</TableCell>
              <TableCell align="center">{team2SecondHalf ?? '-'}</TableCell>
              {game.overtime.ready && (
                <TableCell align="center">{team2Overtime ?? '-'}</TableCell>
              )}
              <TableCell
                align="center"
                sx={{
                  fontWeight: 700,
                  color: winner === 'team2' ? 'success.main' : 'text.primary',
                }}
              >
                {team2Total ?? '-'}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {/* Status messages */}
        {!game.firstHalf.ready && (
          <Alert severity="info" sx={{ mb: 2 }}>
            1. pääty ei ole vielä valmis
          </Alert>
        )}
        {game.firstHalf.ready && !game.secondHalf.ready && (
          <Alert severity="info" sx={{ mb: 2 }}>
            2. pääty ei ole vielä valmis
          </Alert>
        )}
        {needsOvertime && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Tasapeli! Jatkoaika tarvitaan voittajan ratkaisemiseksi.
          </Alert>
        )}
        {isTied && !showOvertime && bothHalvesReady && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Tasapeli. Tässä kierroksessa ei pelata jatkoaikaa.
          </Alert>
        )}
        {winner && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Voittaja: <strong>{winner === 'team1' ? game.team1.name : game.team2.name}</strong>
          </Alert>
        )}
      </Paper>

      <Divider sx={{ my: 2 }} />

      {/* Actions */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {needsOvertime && (
          <Button
            variant="contained"
            color="warning"
            size="large"
            fullWidth
            startIcon={<OvertimeIcon />}
            onClick={onSubmitOvertime}
            disabled={isSubmitting}
            sx={{ py: 2 }}
          >
            Syötä jatkoajan tulokset
          </Button>
        )}

        {canSubmit && (
          <Button
            variant="contained"
            color="success"
            size="large"
            fullWidth
            startIcon={<FinishIcon />}
            onClick={onSubmitScore}
            disabled={isSubmitting}
            sx={{ py: 2 }}
          >
            {isSubmitting ? 'Lähetetään...' : 'Lähetä tulokset'}
          </Button>
        )}

        <Button
          variant="outlined"
          color="error"
          size="large"
          fullWidth
          startIcon={<CancelIcon />}
          onClick={onCancelGame}
          disabled={isSubmitting}
          sx={{ py: 2 }}
        >
          Nollaa peli
        </Button>
      </Box>
    </Box>
  );
}

export default ResultsPanel;

